import React, { useState } from 'react'

import { Box, Chip, Link, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { METER_LABELS, originLabel } from 'features/customers/constants/contactsActivity'
import { useCustomerCreditUnlocks } from 'features/customers/hooks/useCustomerContactsActivity'

import type { Contacts } from '@albacross/types'

import Table from 'components/table/Table'
import type { ColumnConfig } from 'components/table/types'

import { formatUtc } from './formatUtc'
import { searchErrorOf } from './searchError'
import { useClearWhenLinked } from './useClearWhenLinked'
import { useDebouncedSearch } from './useDebouncedSearch'
import { usePageOf } from './usePageOf'

export type ReservationLink = { reservationId: number }

export type ReservationFilter = ReservationLink & { meter: Contacts.CreditMeter }

const columns = (onShowRequest: (reservationId: number) => void): ColumnConfig<Contacts.CreditUnlock>[] => [
  {
    title: 'Person',
    style: { wordBreak: 'break-all' },
    render: row =>
      row.name ? (
        <>
          {row.name}
          <Typography color="text.secondary" component="div" variant="caption">
            {row.personKey}
          </Typography>
        </>
      ) : (
        row.personKey
      ),
  },
  { title: 'Source', field: 'source' },
  {
    title: 'From',
    render: ({ origin, reservationId }) =>
      reservationId ? (
        <Link
          component="button"
          onClick={() => onShowRequest(reservationId)}
          sx={{ textAlign: 'left' }}
          variant="body2">
          {originLabel(origin)}
        </Link>
      ) : (
        originLabel(origin)
      ),
  },
  { title: 'Provider', field: 'provider' },
  { title: 'Charged (UTC)', noBreak: true, render: row => formatUtc(row.createdAt) },
]

type ContactsUnlocksProps = {
  period: string
  reservation: ReservationFilter | null
  onClearReservation: () => void
  onShowRequest: (reservationId: number) => void
}

const ContactsUnlocks = ({ period, reservation, onClearReservation, onShowRequest }: ContactsUnlocksProps) => {
  const [chosenMeter, setChosenMeter] = useState<Contacts.CreditMeter>('email')
  const [pageSize, setPageSize] = useState(25)
  const { text, setText, search, clear } = useDebouncedSearch()

  useClearWhenLinked(reservation, clear)

  // A request charges one meter, so its people are listed under that meter.
  const meter = reservation?.meter ?? chosenMeter

  const { page, setPage } = usePageOf(JSON.stringify([period, meter, search, reservation?.reservationId, pageSize]))

  const { data, isFetching, error } = useCustomerCreditUnlocks({
    period,
    meter,
    ...(search ? { q: search } : {}),
    ...(reservation ? { reservation_id: reservation.reservationId } : {}),
    page: page + 1,
    page_size: pageSize,
  })

  const toolbar = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <ToggleButtonGroup
        aria-label="Emails or phones"
        exclusive
        onChange={(_e, value: Contacts.CreditMeter | null) => {
          if (!value) return
          setChosenMeter(value)
          if (reservation) onClearReservation()
        }}
        size="small"
        value={meter}>
        {(['email', 'phone'] as const).map(value => (
          <ToggleButton key={value} value={value}>
            {METER_LABELS[value]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        label="Search people"
        onChange={e => setText(e.target.value)}
        placeholder="LinkedIn, email, export 812, wf 7, domain"
        size="small"
        sx={{ minWidth: 320 }}
        value={text}
      />
      {reservation && <Chip label={`Request #${reservation.reservationId}`} onDelete={onClearReservation} />}
    </Box>
  )

  return (
    <section aria-label="Charged people" data-testid="contacts-unlocks">
      <Table<Contacts.CreditUnlock>
        config={columns(onShowRequest)}
        count={data?.total}
        data={data?.rows ?? []}
        error={error ? searchErrorOf(error, 'Could not load the charged people') : undefined}
        loading={isFetching && !data}
        minWidth={800}
        onChangePage={setPage}
        onChangeRowsPerPage={value => setPageSize(Number(value))}
        page={page}
        rowsPerPage={pageSize}
        size="small"
        title={`Charged people (${data?.total ?? 0})`}
        toolbar={toolbar}
      />
    </section>
  )
}

export default ContactsUnlocks
